/**
 * Admin Service
 * Handles all admin operations
 */

const BASE_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || '/api')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

const getAuthHeader = () => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
};

const normalizeBatchRow = (row) => {
  if (!row || typeof row !== 'object') {
    return row;
  }

  return {
    ...row,
    supplier_name: row.supplier_name ?? row.supplierName ?? null,
    mfg_date: row.mfg_date ?? row.mfgDate ?? row.manufacturing_date ?? row.manufacturingDate ?? null,
    expiry_date: row.expiry_date ?? row.expiryDate ?? row.exp_date ?? row.expDate ?? null,
    batch_code: row.batch_code ?? row.batchCode ?? row.batch_no ?? row.batchNo ?? null,
    created_by: row.created_by ?? row.createdBy ?? null,
  };
};

// ========== COMPANY INFO ==========

/**
 * Get company information
 */
export const getCompanyInfo = async () => {
  try {
    const response = await fetch(`${BASE_URL}/admin/company-info`);

    if (!response.ok) {
      throw new Error(`Failed to fetch company info: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching company info:', error);
    throw error;
  }
};

/**
 * Update company information
 */
export const updateCompanyInfo = async (companyData) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/company-info`, {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify(companyData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update company info');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating company info:', error);
    throw error;
  }
};

// ========== PRODUCTS ==========

/**
 * Get all products for admin
 */
export const getAdminProducts = async () => {
  try {
    const response = await fetch(`${BASE_URL}/admin/products`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch admin products: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching admin products:', error);
    throw error;
  }
};

/**
 * Get product details for admin
 */
export const getAdminProductById = async (id) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/products/${id}`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch product: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};

/**
 * Get batch history for a product
 */
export const getProductBatches = async (productId) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/products/${productId}/batches`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch product batches: ${response.status}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data.map(normalizeBatchRow) : [];
  } catch (error) {
    console.error('Error fetching product batches:', error);
    throw error;
  }
};

/**
 * Create a new product
 */
export const createProduct = async (formData) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/products`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: formData, // FormData for file upload
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create product');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
};

/**
 * Update product
 */
export const updateProduct = async (id, formData) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/products/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: formData, // FormData for file upload
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update product');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

/**
 * Toggle product status
 */
export const toggleProductStatus = async (id, isActive) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/products/${id}/toggle-status`, {
      method: 'PATCH',
      headers: getAuthHeader(),
      body: JSON.stringify({ is_active: isActive }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to toggle product status');
    }

    return await response.json();
  } catch (error) {
    console.error('Error toggling product status:', error);
    throw error;
  }
};

/**
 * Update product defective count
 */
export const updateProductDefectiveCount = async (id, defectiveCount) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/products/${id}/defective`, {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify({ defective_count: defectiveCount }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update defective count');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating defective count:', error);
    throw error;
  }
};

/**
 * Update used sample count (decrease defective count)
 */
export const updateProductUsedSampleCount = async (id, usedCount) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/products/${id}/defective/used`, {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify({ used_count: usedCount }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update used sample count');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating used sample count:', error);
    throw error;
  }
};

// ========== ORDERS ==========

/**
 * Get all orders for admin
 */
export const getAdminOrders = async () => {
  try {
    const response = await fetch(`${BASE_URL}/admin/orders`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch orders: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

/**
 * Update order status
 */
export const updateOrderStatus = async (orderId, status) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/orders/${orderId}/status`, {
      method: 'PATCH',
      headers: getAuthHeader(),
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update order status');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

/**
 * Update order payment status
 */
export const updateOrderPaymentStatus = async (orderId, isPaid) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/orders/${orderId}/payment`, {
      method: 'PATCH',
      headers: getAuthHeader(),
      body: JSON.stringify({ is_paid: isPaid }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update payment status');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating order payment status:', error);
    throw error;
  }
};

/**
 * Update order item status
 */
export const updateOrderItemStatus = async (orderId, itemId, status) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/orders/${orderId}/items/${itemId}`, {
      method: 'PATCH',
      headers: getAuthHeader(),
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update order item status');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating order item status:', error);
    throw error;
  }
};

/**
 * Get corporate orders
 */
export const getCorporateOrders = async () => {
  try {
    const response = await fetch(`${BASE_URL}/admin/orders/corporate`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch corporate orders: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching corporate orders:', error);
    throw error;
  }
};

// ========== CONTACTS ==========

/**
 * Get all contacts
 */
export const getAdminContacts = async () => {
  try {
    const response = await fetch(`${BASE_URL}/admin/contacts`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch contacts: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching contacts:', error);
    throw error;
  }
};

// ========== OFFERS ==========

/**
 * Get active offers for public pages
 */
export const getPublicOffers = async () => {
  try {
    const response = await fetch(`${BASE_URL}/admin/offers/public`);

    if (!response.ok) {
      throw new Error(`Failed to fetch public offers: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching public offers:', error);
    throw error;
  }
};

/**
 * Get all offers for admin
 */
export const getAdminOffers = async () => {
  try {
    const response = await fetch(`${BASE_URL}/admin/offers`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch offers: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching offers:', error);
    throw error;
  }
};

/**
 * Create an offer
 */
export const createAdminOffer = async (offerData) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/offers`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(offerData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create offer');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating offer:', error);
    throw error;
  }
};

/**
 * Toggle offer active state
 */
export const toggleAdminOffer = async (id, isActive) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/offers/${id}/toggle`, {
      method: 'PATCH',
      headers: getAuthHeader(),
      body: JSON.stringify({ is_active: isActive }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update offer');
    }

    return await response.json();
  } catch (error) {
    console.error('Error toggling offer:', error);
    throw error;
  }
};

/**
 * Delete an offer
 */
export const deleteAdminOffer = async (id) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/offers/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete offer');
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting offer:', error);
    throw error;
  }
};

// ========== ANALYTICS ==========

/**
 * Get analytics dashboard
 */
export const getAnalyticsDashboard = async () => {
  try {
    const response = await fetch(`${BASE_URL}/admin/analytics/dashboard`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch analytics: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching analytics:', error);
    throw error;
  }
};

/**
 * Get product analytics
 */
export const getProductAnalytics = async () => {
  try {
    const response = await fetch(`${BASE_URL}/admin/analytics/products`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch product analytics: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching product analytics:', error);
    throw error;
  }
};

/**
 * Create a product batch (manual stock top-up)
 */
export const createProductBatch = async (productId, batchData) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/products/${productId}/batches`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(batchData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create product batch');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating product batch:', error);
    throw error;
  }
};

/**
 * Update product batch quantity
 */
export const updateProductBatch = async (productId, batchId, batchData) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/products/${productId}/batches/${batchId}`, {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify(batchData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update product batch');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating product batch:', error);
    throw error;
  }
};

// ========== RETURNS ==========

/**
 * Get all returns
 */
export const getAdminReturns = async () => {
  try {
    const response = await fetch(`${BASE_URL}/admin/returns`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch returns: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching returns:', error);
    throw error;
  }
};

/**
 * Create a new return (admin direct creation)
 */
export const createReturn = async (returnData) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/returns`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(returnData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create return');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating return:', error);
    throw error;
  }
};

/**
 * Get return details
 */
export const getAdminReturnById = async (id) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/returns/${id}`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch return: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching return:', error);
    throw error;
  }
};

/**
 * Update return status
 */
export const updateReturnStatus = async (id, status) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/returns/${id}`, {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update return status');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating return status:', error);
    throw error;
  }
};

// ========== INVENTORY ==========

/**
 * Get inventory report
 */
/**
 * Get inventory report with optional filters
 */
export const getInventoryReport = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.product_id) params.append('product_id', filters.product_id);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);

    let url = `${BASE_URL}/admin/inventory/report`;
    if (params.toString()) url += `?${params.toString()}`;

    const response = await fetch(url, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch inventory report: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching inventory report:', error);
    throw error;
  }
};

/**
 * Get inventory transactions with optional filters
 */
export const getInventoryTransactions = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.product_id) params.append('product_id', filters.product_id);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.transaction_type) params.append('transaction_type', filters.transaction_type);

    let url = `${BASE_URL}/admin/inventory/transactions`;
    if (params.toString()) url += `?${params.toString()}`;

    const response = await fetch(url, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch inventory transactions: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching inventory transactions:', error);
    throw error;
  }
};

/**
 * Create corporate order
 */
export const createCorporateOrder = async (orderData) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/orders/corporate`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(orderData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create corporate order');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating corporate order:', error);
    throw error;
  }
};

/**
 * Send corporate order email manually
 */
export const sendCorporateOrderEmail = async (orderId) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/orders/${orderId}/send-email`, {
      method: 'POST',
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send corporate order email');
    }

    return await response.json();
  } catch (error) {
    console.error('Error sending corporate order email:', error);
    throw error;
  }
};

/**
 * Fetch corporate details by organization number via backend API Verket proxy
 */
export const fetchCorporateDetailsByOrganizationNumber = async (organizationNumber) => {
  try {
    const safeValue = encodeURIComponent(String(organizationNumber || '').trim());
    const response = await fetch(`${BASE_URL}/admin/corporate/fetch-by-org/${safeValue}`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to fetch organization details');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching corporate details by organization number:', error);
    throw error;
  }
};

/**
 * Get inventory summary
 */
export const getInventorySummary = async () => {
  try {
    const response = await fetch(`${BASE_URL}/admin/inventory/summary`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch inventory summary: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching inventory summary:', error);
    throw error;
  }
};

// ========== EXPENSES ==========

/**
 * Get all company expenses
 */
export const getAdminExpenses = async () => {
  try {
    const response = await fetch(`${BASE_URL}/admin/expenses`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch expenses: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching expenses:', error);
    throw error;
  }
};

/**
 * Get current-year monthly income vs expenses
 */
export const getAdminExpensesMonthlySummary = async () => {
  try {
    const response = await fetch(`${BASE_URL}/admin/expenses/monthly-summary`, {
      headers: getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch monthly expense summary: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching monthly expense summary:', error);
    throw error;
  }
};

/**
 * Create a company expense
 */
export const createAdminExpense = async (expenseData) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/expenses`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(expenseData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create expense');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating expense:', error);
    throw error;
  }
};

/**
 * Update a company expense (full edit)
 */
export const updateAdminExpense = async (expenseId, expenseData) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/expenses/${expenseId}`, {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify(expenseData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update expense');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating expense:', error);
    throw error;
  }
};

/**
 * Update company expense payment status
 */
export const updateAdminExpensePaymentStatus = async (expenseId, payload) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/expenses/${expenseId}/payment`, {
      method: 'PATCH',
      headers: getAuthHeader(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update expense payment status');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating expense payment status:', error);
    throw error;
  }
};

// ========== INVESTMENTS ==========

export const getAdminInvestments = async () => {
  try {
    const response = await fetch(`${BASE_URL}/admin/investments`, {
      headers: getAuthHeader(),
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch investments: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching investments:', error);
    throw error;
  }
};

export const createAdminInvestment = async (data) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/investments`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create investment');
    }
    return await response.json();
  } catch (error) {
    console.error('Error creating investment:', error);
    throw error;
  }
};

export const updateAdminInvestment = async (investmentId, data) => {
  try {
    const response = await fetch(`${BASE_URL}/admin/investments/${investmentId}`, {
      method: 'PUT',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update investment');
    }
    return await response.json();
  } catch (error) {
    console.error('Error updating investment:', error);
    throw error;
  }
};

const adminServiceExports = {
  getCompanyInfo,
  updateCompanyInfo,
  getAdminProducts,
  getAdminProductById,
  createProduct,
  updateProduct,
  toggleProductStatus,
  updateProductDefectiveCount,
  updateProductUsedSampleCount,
  getAdminOrders,
  updateOrderStatus,
  updateOrderItemStatus,
  getCorporateOrders,
  createCorporateOrder,
  sendCorporateOrderEmail,
  fetchCorporateDetailsByOrganizationNumber,
  getAdminContacts,
  getPublicOffers,
  getAdminOffers,
  createAdminOffer,
  toggleAdminOffer,
  deleteAdminOffer,
  getAnalyticsDashboard,
  getProductAnalytics,
  getAdminReturns,
  getAdminReturnById,
  updateReturnStatus,
  getInventoryReport,
  getInventoryTransactions,
  getInventorySummary,
  getAdminExpenses,
  getAdminExpensesMonthlySummary,
  createAdminExpense,
  updateAdminExpense,
  updateAdminExpensePaymentStatus,
  getAdminInvestments,
  createAdminInvestment,
  updateAdminInvestment,
};

export default adminServiceExports;
